import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import useDeleteProposalLineItem from '@/api/mutations/proposalLineItem/useDeleteProposalLineItem';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import {
  DeleteProposalLineItemMutation,
  ProposalLineItemIdInput,
} from '@/gql/graphql';

import { applyProposalLineItemDeletion } from './applyProposalLineItemDeletion';
import { createOptimisticProposalMutationOptions } from './createOptimisticProposalMutation';

/**
 * Optimistic wrapper around {@link useDeleteProposalLineItem}: removes the line
 * item from the cached proposal in `onMutate` so the row disappears
 * immediately, then rolls back on error. The base hook invalidates the proposal
 * on success, reconciling with server truth.
 */
const useOptimisticDeleteProposalLineItem = (args: {
  proposalId: string;
  options?: UseMutationOptions<
    DeleteProposalLineItemMutation,
    unknown,
    ProposalLineItemIdInput
  >;
}) => {
  const { proposalId, options } = args;
  const queryClient = useQueryClient();
  const queryKey = proposalKeys.proposal(proposalId);

  return useDeleteProposalLineItem({
    proposalId,
    options: {
      ...options,
      ...createOptimisticProposalMutationOptions({
        queryClient,
        queryKey,
        apply: applyProposalLineItemDeletion,
        options,
      }),
    },
  });
};

export default useOptimisticDeleteProposalLineItem;
